import clsx from 'clsx';
import {
  type PropsWithChildren,
  type ReactNode,
  type ReactElement,
  Children,
} from 'react';
import { useMemo } from 'react';
import { isElement } from 'react-is';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type StylingProps,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';
import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import { _isTimeframeSelectorDisplayValueElement } from './DisplayValue.js';
import { useInternalTimeframeSelectorContext } from './hooks/useInternalTimeframeSelectorContext.js';
import { useTimeframeSelectorContext } from './hooks/useTimeframeSelectorContext.js';
import {
  timeframeSelectorSuffixCSS,
  timeframeSelectorSuffixOpenCSS,
  timeframeSelectorTriggerReadOnlyCSS,
} from './TimeframeSelector.sty.js';

/**
 * @public
 */
export type TimeframeSelectorTriggerProps = PropsWithChildren &
  StylingProps &
  DataTestId &
  AriaLabelingProps & {
    /**
     * The placeholder text displayed in the TimeframeSelector.Trigger.
     */
    placeholder?: ReactNode;
  };

/**
 * Typeguard function that will check if a ReactNode is a TimeframeSelector.Trigger
 * @internal
 */
export function _isTimeframeSelectorTriggerElement(
  child: ReactNode,
): child is ReactElement<TimeframeSelectorTriggerProps> {
  return isElement(child) && child.type === Trigger;
}

function getDisplayValueSlot(children: ReactNode) {
  let displayValue: ReactNode[] | null = null;

  Children.forEach(children, (child) => {
    if (_isTimeframeSelectorDisplayValueElement(child)) {
      displayValue = displayValue || [];
      displayValue.push(child);
    }
  });

  return displayValue;
}

/**
 * A trigger component to customize a timeframe selector's trigger with custom styling, placeholder, aria labels, and data-testid.
 * If it doesn't have children, the default trigger is used. OTherwise, the `TimeframeSelector.DisplayValue` component can be used within it to define a custom display value within.
 * @public
 */
export const Trigger = (props: TimeframeSelectorTriggerProps) => {
  const {
    children,
    placeholder,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  const displayValueSlot = useMemo(
    () => getDisplayValueSlot(children),
    [children],
  );

  const {
    triggerProps,
    displayValue: contextDisplayValue,
    details,
  } = useTimeframeSelectorContext();
  const { readOnly, showError } = useInternalTimeframeSelectorContext();

  const displayValue = displayValueSlot ?? contextDisplayValue;
  const placeholderIfEmpty = placeholder ?? contextDisplayValue;

  return (
    <Button
      {..._mergeProps(triggerProps, remainingProps)}
      data-testid={dataTestId}
      variant="emphasized"
      color={showError ? 'critical' : 'neutral'}
      style={consumerStyle}
      className={clsx(
        consumerClassName,
        readOnly && timeframeSelectorTriggerReadOnlyCSS,
      )}
    >
      <Button.Suffix>
        <ChevronDownSmallIcon
          className={clsx(
            timeframeSelectorSuffixCSS,
            triggerProps?.['aria-expanded'] && timeframeSelectorSuffixOpenCSS,
          )}
        />
      </Button.Suffix>
      {details ? displayValue : placeholderIfEmpty}
    </Button>
  );
};

Trigger.displayName = 'TimeframeSelector.Trigger';
